import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormSpy } from 'react-final-form';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { breakpointSelector } from 'selectors/breakpoint';

// place GuidedFormIndicator at the bottom of your final-form render function
// make sure to `import setFieldData from 'final-form-set-field-data';`
// and pass it as `mutators={{ setFieldData }}` to the <Form />
// see BookingWidget.js for a working example
// it will set the `meta.data.isFocusedActive` to the first invalid field

const GuidedFormIndicator = ({ disableGuidedFlow, form, formRef }) => {
  const breakpoint = useSelector(breakpointSelector);
  const [previousIndicatedField, setPreviousIndicatedField] = useState(null);
  const [userEndedGuidedFlow, setUserEndedGuidedFlow] = useState(false);
  const lastFormState = useRef(null);
  const indicatedFieldChanged = useRef(false);
  const currentIndicatedField = useRef(null);

  const getOrderedFieldNames = useCallback((formElement) => {
    const collection = utils.get(formElement, 'current.elements') || [];
    return [...collection].filter((e) => e.name).map((e) => e.name);
  }, []);

  const setIndicatedField = useCallback(
    (formState) => {
      const { setFieldData } = form.mutators;
      const { active, errors, dirty } = formState;
      const shouldIndicate = !!(previousIndicatedField && !breakpoint.isMobile && !disableGuidedFlow);
      const registeredFields = getOrderedFieldNames(formRef);
      const invalidFieldKeys = Object.keys(errors);

      // check if the fields are being activated/filled in order
      // if an error field index is lower than the active field index, this indicates the user broke the flow order
      const activeFieldIndex = registeredFields.indexOf(active);
      const [firstInvalidField] = invalidFieldKeys;
      const firstInvalidFieldIndex = registeredFields.indexOf(firstInvalidField);
      const userSkippedOrder = userEndedGuidedFlow || activeFieldIndex > firstInvalidFieldIndex;

      // indicate the first invalid field
      let indicatedField = null;
      registeredFields.forEach((key) => {
        if (!indicatedField && invalidFieldKeys.includes(key)) {
          setFieldData(key, {
            isGuided: true,
            isNext: true,
            isFocusedActive: shouldIndicate,
          });

          indicatedField = key;
        } else {
          setFieldData(key, {
            isGuided: true,
            isNext: false,
            isFocusedActive: false,
          });
        }
      });

      indicatedFieldChanged.current = indicatedField && indicatedField !== previousIndicatedField;
      currentIndicatedField.current = indicatedField;
      lastFormState.current = { shouldIndicate, dirty, userSkippedOrder };

      setPreviousIndicatedField(indicatedField);
      setUserEndedGuidedFlow(userSkippedOrder);
    },
    [
      breakpoint,
      disableGuidedFlow,
      form.mutators,
      formRef,
      previousIndicatedField,
      userEndedGuidedFlow,
      getOrderedFieldNames,
    ]
  );

  useEffect(() => {
    if (indicatedFieldChanged.current && lastFormState.current && currentIndicatedField.current) {
      const { shouldIndicate, dirty } = lastFormState.current;

      const shouldFocusAndActivate = shouldIndicate && dirty && !userEndedGuidedFlow;

      if (shouldFocusAndActivate) {
        const [field] = document.getElementsByName(currentIndicatedField.current);
        setTimeout(() => {
          field && field.focus();
        }, 0);
      }

      indicatedFieldChanged.current = false;
    }
  }, [previousIndicatedField, userEndedGuidedFlow]);

  const handleChange = useCallback(
    (formState) => {
      setIndicatedField(formState);
    },
    [setIndicatedField]
  );

  // hack for handleChange to get called  before render, referred from here https://github.com/final-form/react-final-form/issues/809#issuecomment-808942373
  return (
    <FormSpy
      onChange={(formState) => setTimeout(() => handleChange(formState), 0)}
      subscription={{ active: true, errors: true, submitFailed: true, dirty: true }}
    />
  );
};

GuidedFormIndicator.propTypes = {
  form: PropTypes.any,
};

export default GuidedFormIndicator;
